/**
 * Path2D caching and spatial indexing utilities
 */

import RBush from 'rbush'
import type { CanvasElement, SpatialItem, SpatialIndex } from './types'

/**
 * Get or create a cached Path2D object for an element
 */
export function getOrCreatePath(
  pathCache: Map<string, Path2D>,
  element: CanvasElement
): Path2D {
  let path = pathCache.get(element.id)
  if (!path) {
    path = new Path2D(element.pathData)
    pathCache.set(element.id, path)
  }
  return path
}

/**
 * Build spatial index items from elements
 */
export function buildSpatialItems(elements: CanvasElement[]): SpatialItem[] {
  return elements.map(element => ({
    minX: element.bounds.x,
    minY: element.bounds.y,
    maxX: element.bounds.x + element.bounds.width,
    maxY: element.bounds.y + element.bounds.height,
    element
  }))
}

/**
 * Create a new spatial index
 */
export function createSpatialIndex(): SpatialIndex {
  return new RBush<SpatialItem>()
}

/**
 * Query spatial index for elements within a viewport
 */
export function queryVisibleElements(
  spatialIndex: SpatialIndex,
  viewport: { x: number; y: number; width: number; height: number }
): CanvasElement[] {
  const results = spatialIndex.search({
    minX: viewport.x,
    minY: viewport.y,
    maxX: viewport.x + viewport.width,
    maxY: viewport.y + viewport.height
  })
  return results.map(item => item.element)
}

/**
 * Check if element bounds intersect with a viewport rectangle
 */
export function intersectsViewport(
  bounds: { x: number; y: number; width: number; height: number },
  viewport: { x: number; y: number; width: number; height: number }
): boolean {
  return !(
    bounds.x + bounds.width < viewport.x ||
    bounds.y + bounds.height < viewport.y ||
    bounds.x > viewport.x + viewport.width ||
    bounds.y > viewport.y + viewport.height
  )
}
